import { cloneDeep, flattenDeep } from 'lodash-es';

import { getSafe, isNilOrEmpty } from 'helpers';
import { flattenRules } from 'query-builder/core/flatten-rules';
import { removeEmptyRuleSets } from 'query-builder/core/remove-empty-rulesets';
import {
  getDefaultTagCategoryResponsesRuleSet, getDefaultTagsAdvancedRule
} from 'query-builder/core/simple-query-default-ruleset';
import { Rule, RuleSet } from 'query-builder/query.interfaces';

import {
  IEntityConnectionFieldValueViewModel, IEntityConnectionSubqueryQueryField
} from '../components/entity-connection-subquery-input/EntityConnectionFieldViewModel';
import {
  TAG_CATEGORY_COMBO_UI_CONDITION_FIELD
} from '../rule-interfaces/TagCategoryResponsesRuleField';

export const CONNECTION_TO_ENTITY_ID_FIELD = 'connection_to_entity_id';

export class EntityConnectionSubqueryHelpers {

  static toSimpleConnectionsUIValue(ruleValue: IEntityConnectionSubqueryQueryField['value']): IEntityConnectionFieldValueViewModel {
    const tagsComboRuleSet = this.extractAdvancedTagRuleSet(ruleValue);
    const tagCategoryResponsesRuleSet = this.extractTagCategoryResponseRuleSet(ruleValue);

    return cloneDeep({
      uiTagsOrCategories: ruleValue.uiTagsOrCategories || 'tags',
      entityIds: getSafe(() => this.extractConnectionToEntityIds(ruleValue.subquery), []),
      tagsComboRuleSet,
      tagCategoryResponsesRuleSet,
      subqueryCondition: ruleValue.subquery?.condition || 'or',
      include_inactive: ruleValue?.include_inactive,
      hasConnections: ruleValue?.has_connections,
      entityConnectionTypeId: ruleValue?.entity_connection_type_ids?.[0],
      createdBy: ruleValue?.created_by || [],
      createdDateRange: {
        relativeOffset: ruleValue?.relative_offset,
        startDate: ruleValue?.created_after_date,
        endDate: ruleValue?.created_before_date
      }
    });
  }

  static toComplexConnectionsRuleFormValue(viewModel: IEntityConnectionFieldValueViewModel): IEntityConnectionSubqueryQueryField['value'] {
    const hasTagAdvancedRule = !isNilOrEmpty(viewModel.tagsComboRuleSet);
    const subqueryRules = [];

    if (viewModel.entityIds?.length > 0) {
      subqueryRules.push({
        field: CONNECTION_TO_ENTITY_ID_FIELD,
        operator: 'in',
        value: viewModel.entityIds
      });
    }

    if (viewModel.uiTagsOrCategories === 'tag_category') {
      subqueryRules.push(viewModel.tagCategoryResponsesRuleSet);
    } else {
      if (hasTagAdvancedRule) {
        viewModel.tagsComboRuleSet.ui_condition_field = 'tags_combo';
        subqueryRules.push(viewModel.tagsComboRuleSet);
      }
    }

    const subquery = removeEmptyRuleSets({
      condition: 'and',
      rules: subqueryRules
    });

    return {
      uiTagsOrCategories: viewModel.uiTagsOrCategories,
      include_inactive: !!viewModel.include_inactive,
      created_by: viewModel.createdBy,
      relative_offset: viewModel.createdDateRange?.relativeOffset,
      created_after_date: viewModel.createdDateRange?.startDate,
      created_before_date: viewModel.createdDateRange?.endDate,
      has_connections: viewModel.hasConnections ? true : undefined,
      entity_connection_type_ids: viewModel.entityConnectionTypeId ? [viewModel.entityConnectionTypeId] : [],
      subquery: subquery as any
    };
  }

  static extractTagCategoryResponseRuleSet(connectionFieldRuleValue: IEntityConnectionSubqueryQueryField['value']): RuleSet {
    const subquery = connectionFieldRuleValue?.subquery;
    const subqueryRules = subquery?.rules;
    const defaultRuleset = getDefaultTagCategoryResponsesRuleSet('entity_connection');

    const tagResponsesRuleSet = (subqueryRules || []).find(
      (r: Rule | RuleSet) => r['ui_condition_field'] === TAG_CATEGORY_COMBO_UI_CONDITION_FIELD
    ) as RuleSet;

    if (tagResponsesRuleSet) {
      tagResponsesRuleSet.ui_condition_field = TAG_CATEGORY_COMBO_UI_CONDITION_FIELD;
    }
    return tagResponsesRuleSet || defaultRuleset;
  }

  static extractAdvancedTagRuleSet(connectionFieldRuleValue: IEntityConnectionSubqueryQueryField['value']): RuleSet {
    const subquery = connectionFieldRuleValue?.subquery;
    const subqueryRules = subquery?.rules;

    const defaultRuleset: RuleSet = {
      condition: 'and',
      ui_condition_field: 'tags_combo',
      rules: [
        getDefaultTagsAdvancedRule('entity_connection', 'include having any'),
        getDefaultTagsAdvancedRule('entity_connection', 'exclude having any'),
      ]
    };

    const tagAdvancedRuleSet = (subqueryRules || []).find(
      (r: Rule | RuleSet) => r['ui_condition_field'] === 'tags_combo'
    ) as RuleSet;

    if (tagAdvancedRuleSet) {
      tagAdvancedRuleSet.ui_condition_field = 'tags_combo';
    }
    return tagAdvancedRuleSet || defaultRuleset;
  }

  static extractConnectionToEntityIds(subquery: RuleSet) {
    return flattenDeep(flattenRules(subquery?.rules)
      .filter(rule => rule.field === CONNECTION_TO_ENTITY_ID_FIELD)
      .map(rule => rule.value));
  }

  static createConnectedToQuery(entityId: string, connectedToEntityTypeId: string, includeInactive = false): RuleSet {
    return {
      condition: 'and',
      rules: [
        {
          field: 'entity_type',
          operator: 'in',
          value: [connectedToEntityTypeId]
        },
        {
          field: 'entity_connection_subquery',
          operator: 'include',
          value: this.toComplexConnectionsRuleFormValue({
            uiTagsOrCategories: 'tags',
            entityIds: [entityId],
            createdBy: [],
            entityConnectionTypeId: null,
            hasConnections: undefined,
            createdDateRange: null,
            include_inactive: includeInactive,
            tagsComboRuleSet: null,
            tagCategoryResponsesRuleSet: null
          })
        }
      ]
    };
  }
}
